import { createContext, useContext } from 'react'

import { Country } from '@cash-web/protos/squareup/common/countries.pb'

export type Region = `${Country}`

export type TypedRegionConfig<SingleRegionConfig> = { [key in Region]: SingleRegionConfig }

type RegionConfig = { [key in Region]: unknown }
type RegionContextType = {
  region?: Region
  regionConfig?: RegionConfig
  setContextRegion: React.Dispatch<React.SetStateAction<Region | undefined>>
  supportedRegions?: Region[]
}

export const RegionContext = createContext<RegionContextType | undefined>(undefined)

/**
 * Returns a country code, probably from the region guess, but this
 * is not guaranteed.
 */
export function useCountryFromRegionProvider(): Region | undefined {
  const context = useContext(RegionContext)

  if (!context) {
    throw Error('useRegion: must be used within a RegionProvider')
  }

  return context.region
}

/**
 * Returns a country code. Yes, the naming is bad.
 *
 * @deprecated Switch to useCountryFromRegionProvider(), which is more accurately named
 *             and inspires a bit more fear.
 */
export function useRegion(): Region | undefined {
  return useCountryFromRegionProvider()
}

/**
 * All region configurations are memoized and available from the Provider.
 * This method returns a single region configuration from the configuration object.
 *
 * @param customRegion, you can specify a region configuration or the default will be the current region
 * @returns a single region configuration
 */
export function useRegionConfig<SingleRegionConfig>(customRegion?: Region): SingleRegionConfig {
  const context = useContext(RegionContext)

  if (!context) {
    throw Error('useRegionConfig: must be used within a RegionProvider')
  }

  if (!context.regionConfig) {
    throw Error('useRegionConfig: RegionProvider was initialized without a region config')
  } else {
    if (customRegion) {
      if (context.regionConfig?.[customRegion] === undefined) {
        throw Error('useRegionConfig: must pass in a supported region for your configuration')
      }
      return context.regionConfig?.[customRegion] as SingleRegionConfig
    }
    return context.regionConfig[context.region as Region] as SingleRegionConfig
  }
}

export function useSetContextRegion() {
  const context = useContext(RegionContext)

  if (!context) {
    throw Error(`useSetContextRegion: must be used within a useRegionProvider`)
  }

  return context.setContextRegion
}
